import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  useApprovalTypeFixedApprovers,
  useApprovalTypeFixedCoApprovers,
  useApprovalTypeFixedReferrers,
} from '@/hooks/approval/useApprovalType';
import { MyInfoState } from '@/recoil/atom';
import type { ApprovalTypeReferrer } from '@/types/approval';

/**
 * 양식 고정 항목에서 작성자 본인을 제외한 목록.
 *
 * BE 가 본인을 결재자/협조자로 거부하므로(422 "작성자 본인을 결재자/협조자로 지정할 수 없습니다")
 * 잠긴 채 남겨두면 그 양식은 임시저장·상신 자체가 불가능해진다. 양식 메타에 이를 구분할 플래그가
 * 없어 FE 에서 일괄 제외한다.
 *
 * 참조인 섹션과 결재라인 섹션이 같은 기준으로 판별해야 하므로 여기 한곳에서 만든다.
 */
const useExcludeSelf = (
  members: ApprovalTypeReferrer[],
): ApprovalTypeReferrer[] => {
  const { passportId: myPassportId } = useAtomValue(MyInfoState);
  return useMemo(
    () =>
      myPassportId ? members.filter((r) => r.id !== myPassportId) : members,
    [members, myPassportId],
  );
};

/** 양식 고정 결재자 (작성자 본인 제외) */
export const useFormFixedApprovers = (
  apvType?: number | null,
): ApprovalTypeReferrer[] =>
  useExcludeSelf(useApprovalTypeFixedApprovers(apvType ?? null));

/** 양식 고정 협조결재자 (작성자 본인 제외) */
export const useFormFixedCoApprovers = (
  apvType?: number | null,
): ApprovalTypeReferrer[] =>
  useExcludeSelf(useApprovalTypeFixedCoApprovers(apvType ?? null));

/** 양식 고정 참조인 (작성자 본인 제외) */
export const useFormFixedReferrers = (
  apvType?: number | null,
): ApprovalTypeReferrer[] =>
  useExcludeSelf(useApprovalTypeFixedReferrers(apvType ?? null));
